import { useEffect, useMemo, useState } from "react";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { Button, Group, Input, PaginadorTabla, Stack } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { CentroDeCosto } from "@/lib/types";
import { importarCentrosDeCosto, listarCentrosDeCostoPaginado } from "../services/catalogos-contables-admin-service";
import { useCecosAdmin } from "../hooks/use-catalogos-contables-admin";
import { useCrearCeco, useEditarCeco, useImportarCecos } from "../hooks/use-catalogos-contables-mutations";
import { ImportarExcelCatalogo } from "./ImportarExcelCatalogo";
import { ItemCatalogoCrearForm } from "./ItemCatalogoCrearForm";
import { ItemCatalogoTable } from "./ItemCatalogoTable";

const PAGE_SIZE = 15;

const EJEMPLOS_CECOS = [
  { codigo: "7220061800", nombre: "Tesorería (ejemplo · reemplázame)" },
  { codigo: "CC-001", nombre: "Administración (ejemplo · reemplázame)" },
];

/** Gestor de CeCo · búsqueda + paginado SERVER-SIDE (los pickers del wizard no cambian). */
export function SeccionCecos() {
  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(1);
  const [busquedaDebounced] = useDebouncedValue(busqueda, 350);

  const serverParams = useMemo(
    () => ({
      ...(busquedaDebounced.trim() ? { busqueda: busquedaDebounced.trim() } : {}),
      page,
      pageSize: PAGE_SIZE,
    }),
    [busquedaDebounced, page]
  );

  useEffect(() => {
    setPage(1);
  }, [busquedaDebounced]);

  const { data } = useCecosAdmin(serverParams);
  const cecos = data?.items ?? [];
  const total = data?.total ?? 0;

  const [modoCrear, setModoCrear] = useState(false);
  const crear = useCrearCeco({ onCreado: () => setModoCrear(false) });
  const editar = useEditarCeco();
  const importar = useImportarCecos();

  const obtenerTodos = async (): Promise<CentroDeCosto[]> => {
    const b = busquedaDebounced.trim();
    const todos: CentroDeCosto[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarCentrosDeCostoPaginado({ ...(b ? { busqueda: b } : {}), page: p, pageSize: 100 });
      todos.push(...pagina.items);
      if (todos.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todos;
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Input placeholder="Buscar por código o nombre…" leftSection={<IconSearch size={14} stroke={1.7} />} value={busqueda} onChange={(e) => setBusqueda(e.currentTarget.value)} wrapperClassName="w-70" />
        <Group gap="xs">
          <ImportarExcelCatalogo importar={importar} previsualizar={(filas) => importarCentrosDeCosto(filas, true)} plantillaArchivo="plantilla-centros-de-costo" plantillaHoja="Centros de costo" ejemplos={EJEMPLOS_CECOS} />
          {!modoCrear ? (
            <Button leftSection={<IconPlus size={14} />} onClick={() => setModoCrear(true)}>
              Nuevo CeCo
            </Button>
          ) : null}
        </Group>
      </Group>
      {modoCrear ? (
        <ItemCatalogoCrearForm
          copy={{ titulo: "Nuevo Centro de Costo", ejemploCodigo: "CC-001", ejemploNombre: "Tesorería" }}
          loading={crear.isPending}
          onCancelar={() => setModoCrear(false)}
          onCrear={crear.mutate}
        />
      ) : null}
      <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
      <ItemCatalogoTable items={cecos} nombreExport="centros-de-costo" obtenerFilasExport={obtenerTodos} onGuardar={editar.mutate} isPending={editar.isPending} />
      <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="CeCos" />
    </Stack>
  );
}
